// frontend/src/pages/configuracoes/empresas/GerenciarEmpresas.js
//
// Cadastro de empresas (tenants). Segue o padrão das demais telas de
// configuração: formulário em cima, lista embaixo, ConfirmationModal para
// confirmar e SuccessModal para o retorno positivo.
//
// Permissões:
//   VISUALIZAR_EMPRESA  — abre a tela (o backend devolve só a própria empresa
//                         para quem não tem GERENCIAR_EMPRESAS);
//   ATUALIZAR_EMPRESA   — editar dados e logo;
//   GERENCIAR_EMPRESAS  — criar, excluir e replicar cadastros de apoio.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Template from "../../../components/Template";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import FormLabel from "../../../components/FormLabel";
import empresaService from "../../../services/empresaService";
import usePermission from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContext";
import { maskCpfCnpj, maskCep, maskPhone } from "../../../utils/masks";
import config from "../../../config";

const ESTADO_INICIAL_FORM = {
    nome: "",
    razaoSocial: "",
    nomeFantasia: "",
    cnpj: "",
    cnpjsAdicionais: "",
    inscricaoEstadual: "",
    inscricaoMunicipal: "",
    endereco: "",
    bairro: "",
    municipio: "",
    uf: "",
    cep: "",
    telefone: "",
    email: "",
    ativo: true,
    // Só usados na criação:
    tenantModeloId: "",
    replicarCatalogo: true,
    criarAdministrador: false,
    administradorNome: "",
    administradorEmail: "",
    administradorSenha: "",
};

const classeCampo = (temErro) =>
    `px-3 py-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-400 ${temErro ? "border-red-600" : "border-slate-300"
    }`;

const classeBotao =
    "px-4 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-stone-500 disabled:opacity-60 disabled:cursor-not-allowed";

/** Validação de CNPJ (mesmos dígitos verificadores conferidos pelo backend). */
const cnpjValido = (valor) => {
    const cnpj = String(valor || "").replace(/\D/g, "");
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cnpj)) return false;

    const calcular = (base, pesos) => {
        const soma = base
            .split("")
            .reduce((total, digito, i) => total + parseInt(digito, 10) * pesos[i], 0);
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };

    const primeiro = calcular(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const segundo = calcular(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    return primeiro === parseInt(cnpj[12], 10) && segundo === parseInt(cnpj[13], 10);
};

/**
 * A qual campo pertence a mensagem devolvida pelo backend. Serve para mostrar o
 * erro ao lado do campo culpado em vez de só numa faixa no topo. A ordem importa:
 * "CNPJ adicional inválido" também casa com /cnpj/.
 */
const CAMPO_POR_MENSAGEM = [
    { padrao: /adicion/i, campo: "cnpjsAdicionais" },
    { padrao: /nome da empresa|empresa com o nome/i, campo: "nome" },
    { padrao: /cnpj/i, campo: "cnpj" },
    { padrao: /^uf /i, campo: "uf" },
    { padrao: /e-?mail/i, campo: "administradorEmail" },
    { padrao: /senha/i, campo: "administradorSenha" },
];

const campoDaMensagem = (msg) => {
    if (!msg) return null;
    const encontrado = CAMPO_POR_MENSAGEM.find((item) => item.padrao.test(msg));
    return encontrado ? encontrado.campo : null;
};

/** Uma lista de CNPJs por linha vira um array de dígitos, sem repetição. */
const listaDeCnpjs = (texto) => [
    ...new Set(
        String(texto || "")
            .split(/[,;\n]/)
            .map((item) => item.replace(/\D/g, ""))
            .filter(Boolean)
    ),
];

/** Mensagem de erro da API: sempre o `msg` do backend, nunca o stack. */
const mensagemDoErro = (error, alternativa) =>
    error?.response?.data?.msg || alternativa;

const rotulosTotais = {
    usuarios: "usuários",
    obras: "obras",
    pedidos: "pedidos",
    notas: "notas",
    clientes: "clientes",
    fornecedores: "fornecedores",
};

const GerenciarEmpresas = () => {
    const navigate = useNavigate();
    const { auth, recarregarEmpresa } = useAuth();

    const podeGerenciarTodas = usePermission("GERENCIAR_EMPRESAS");
    const podeAtualizar = usePermission("ATUALIZAR_EMPRESA");

    const empresaDoUsuarioId = auth?.user?.tenantId;

    const [empresas, setEmpresas] = useState([]);
    const [carregandoLista, setCarregandoLista] = useState(true);
    const [filtros, setFiltros] = useState({ nome: "", ativo: "" });

    const [formData, setFormData] = useState(ESTADO_INICIAL_FORM);
    const [errors, setErrors] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [salvando, setSalvando] = useState(false);

    // Mensagem de erro exibida em faixa no topo (o backend manda `msg` pronto).
    const [erroMensagem, setErroMensagem] = useState("");

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [empresaParaExcluir, setEmpresaParaExcluir] = useState(null);
    const [showConfirmProvisionar, setShowConfirmProvisionar] = useState(false);
    const [empresaParaProvisionar, setEmpresaParaProvisionar] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // Logo (só faz sentido em edição, porque depende do id da empresa).
    const [logoArquivo, setLogoArquivo] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [logoSalvando, setLogoSalvando] = useState(false);
    const [logoPreviewErro, setLogoPreviewErro] = useState(false);
    const logoInputRef = useRef(null);

    const formRef = useRef(null);

    const mostrarFormulario = podeGerenciarTodas || (isEditMode && podeAtualizar);

    const carregarEmpresas = useCallback(async (filtrosBusca = { nome: "", ativo: "" }) => {
        setCarregandoLista(true);
        try {
            const dados = await empresaService.listar(filtrosBusca);
            setEmpresas(dados.records || []);
            setErroMensagem("");
        } catch (error) {
            console.error("Erro ao carregar empresas:", error);
            setEmpresas([]);
            setErroMensagem(mensagemDoErro(error, "Não foi possível carregar as empresas."));
        } finally {
            setCarregandoLista(false);
        }
    }, []);

    useEffect(() => {
        carregarEmpresas();
    }, [carregarEmpresas]);

    // ===================== Formulário =====================

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleCnpjChange = (e) => {
        setFormData((prev) => ({ ...prev, cnpj: maskCpfCnpj(e.target.value) }));
        setErrors((prev) => ({ ...prev, cnpj: undefined }));
    };

    // A máscara é aplicada ao sair do campo: reformatar a cada tecla numa área
    // de texto com várias linhas jogaria o cursor para o fim a cada digitação.
    const handleCnpjsAdicionaisBlur = () => {
        const formatados = listaDeCnpjs(formData.cnpjsAdicionais).map(maskCpfCnpj).join("\n");
        setFormData((prev) => ({ ...prev, cnpjsAdicionais: formatados }));
    };

    const handleCepChange = (e) => {
        setFormData((prev) => ({ ...prev, cep: maskCep(e.target.value) }));
    };

    const handleTelefoneChange = (e) => {
        setFormData((prev) => ({ ...prev, telefone: maskPhone(e.target.value) }));
    };

    const handleUfChange = (e) => {
        const valor = e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 2);
        setFormData((prev) => ({ ...prev, uf: valor }));
        setErrors((prev) => ({ ...prev, uf: undefined }));
    };

    const validarFormulario = () => {
        const novosErros = {};

        if (!formData.nome.trim()) {
            novosErros.nome = "Informe o nome da empresa.";
        }

        if (formData.cnpj.trim() && !cnpjValido(formData.cnpj)) {
            novosErros.cnpj = "CNPJ inválido.";
        }

        const adicionais = listaDeCnpjs(formData.cnpjsAdicionais);
        const principal = formData.cnpj.replace(/[^0-9]/g, "");
        const invalido = adicionais.find((item) => !cnpjValido(item));
        if (invalido) {
            novosErros.cnpjsAdicionais = `CNPJ adicional inválido: ${maskCpfCnpj(invalido)}.`;
        } else if (principal && adicionais.includes(principal)) {
            novosErros.cnpjsAdicionais = "O CNPJ adicional não pode repetir o CNPJ principal.";
        }

        if (formData.uf.trim() && formData.uf.trim().length !== 2) {
            novosErros.uf = "UF deve ter 2 letras.";
        }

        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            novosErros.email = "E-mail inválido.";
        }

        if (!isEditMode && formData.criarAdministrador) {
            if (!formData.administradorNome.trim()) {
                novosErros.administradorNome = "Informe o nome do administrador.";
            }
            if (!formData.administradorEmail.trim()) {
                novosErros.administradorEmail = "Informe o e-mail do administrador.";
            } else if (!/\S+@\S+\.\S+/.test(formData.administradorEmail)) {
                novosErros.administradorEmail = "E-mail inválido.";
            }
            if (!formData.administradorSenha) {
                novosErros.administradorSenha = "Informe a senha do administrador.";
            } else if (formData.administradorSenha.length < 8) {
                novosErros.administradorSenha = "A senha deve ter ao menos 8 caracteres.";
            }
        }

        setErrors(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        setErroMensagem("");
        if (validarFormulario()) {
            setShowConfirmDialog(true);
        }
    };

    const montarPayload = () => {
        const payload = {
            nome: formData.nome.trim(),
            razaoSocial: formData.razaoSocial.trim(),
            nomeFantasia: formData.nomeFantasia.trim(),
            cnpj: formData.cnpj.replace(/\D/g, ""),
            cnpjsAdicionais: listaDeCnpjs(formData.cnpjsAdicionais),
            inscricaoEstadual: formData.inscricaoEstadual.trim(),
            inscricaoMunicipal: formData.inscricaoMunicipal.trim(),
            endereco: formData.endereco.trim(),
            bairro: formData.bairro.trim(),
            municipio: formData.municipio.trim(),
            uf: formData.uf.trim(),
            cep: formData.cep.trim(),
            telefone: formData.telefone.trim(),
            email: formData.email.trim(),
            ativo: formData.ativo,
        };

        if (!isEditMode) {
            payload.replicarCatalogo = formData.replicarCatalogo;
            if (formData.tenantModeloId) {
                payload.tenantModeloId = parseInt(formData.tenantModeloId, 10);
            }
            if (formData.criarAdministrador) {
                payload.administrador = {
                    nome: formData.administradorNome.trim(),
                    email: formData.administradorEmail.trim(),
                    senha: formData.administradorSenha,
                };
            }
        }

        return payload;
    };

    const handleConfirmSubmit = async () => {
        // Guarda contra duplo clique: o modal fecha e o botão fica desabilitado,
        // mas uma segunda confirmação em voo não pode virar dois cadastros.
        if (salvando) return;

        setShowConfirmDialog(false);
        setSalvando(true);
        setErroMensagem("");

        try {
            if (isEditMode && editingId) {
                await empresaService.atualizar(editingId, montarPayload());

                // A logo escolhida vai junto do Salvar.
                //
                // Antes ela dependia de um segundo clique em "Enviar logo": quem
                // selecionava o arquivo via a pré-visualização preencher, salvava
                // o formulário e ia embora convencido de ter gravado — mas nenhuma
                // requisição de logo chegava a sair. Um formulário com dois botões
                // de salvar, um deles para um único campo, não se sustenta.
                const enviouLogo = Boolean(logoArquivo);
                if (enviouLogo) {
                    await empresaService.enviarLogo(editingId, logoArquivo);
                    setLogoArquivo(null);
                    if (logoInputRef.current) logoInputRef.current.value = "";
                }

                setSuccessMessage(
                    enviouLogo
                        ? "Empresa e logo atualizadas com sucesso!"
                        : "Empresa atualizada com sucesso!"
                );
                if (editingId === empresaDoUsuarioId && recarregarEmpresa) {
                    // O nome e a logo mostrados no cabeçalho e no menu precisam
                    // acompanhar a edição.
                    recarregarEmpresa();
                }
            } else {
                const criada = await empresaService.criar(montarPayload());
                setSuccessMessage(
                    criada?.administrador
                        ? `Empresa "${criada.nome}" criada, cadastros de apoio replicados e administrador ${criada.administrador.email} cadastrado.`
                        : `Empresa "${criada.nome}" criada e cadastros de apoio replicados.`
                );
            }

            setShowSuccessDialog(true);
            limparFormulario();
            await carregarEmpresas(filtros);
        } catch (error) {
            console.error("Erro ao salvar empresa:", error);
            const msg = mensagemDoErro(
                error,
                `Erro ao ${isEditMode ? "atualizar" : "cadastrar"} a empresa. Verifique os dados e tente novamente.`
            );
            // Quando dá para saber de qual campo o backend está falando, a
            // mensagem aparece ao lado dele; caso contrário, na faixa do topo.
            const campo = error?.response?.data?.msg ? campoDaMensagem(msg) : null;
            if (campo) {
                setErrors((prev) => ({ ...prev, [campo]: msg }));
                document.getElementById(campo)?.focus();
            } else {
                setErroMensagem(msg);
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setSalvando(false);
        }
    };

    const limparFormulario = () => {
        setFormData(ESTADO_INICIAL_FORM);
        setErrors({});
        setIsEditMode(false);
        setEditingId(null);
        setLogoArquivo(null);
        setLogoPreview("");
        if (logoInputRef.current) logoInputRef.current.value = "";
    };

    const handleEdit = (empresa) => {
        setIsEditMode(true);
        setEditingId(empresa.id);
        setErroMensagem("");
        setErrors({});
        setFormData({
            ...ESTADO_INICIAL_FORM,
            nome: empresa.nome || "",
            razaoSocial: empresa.razaoSocial || "",
            nomeFantasia: empresa.nomeFantasia || "",
            cnpj: empresa.cnpj ? maskCpfCnpj(empresa.cnpj) : "",
            cnpjsAdicionais: (empresa.cnpjsAdicionais || []).map(maskCpfCnpj).join("\n"),
            inscricaoEstadual: empresa.inscricaoEstadual || "",
            inscricaoMunicipal: empresa.inscricaoMunicipal || "",
            endereco: empresa.endereco || "",
            bairro: empresa.bairro || "",
            municipio: empresa.municipio || "",
            uf: empresa.uf || "",
            cep: empresa.cep ? maskCep(empresa.cep) : "",
            telefone: empresa.telefone ? maskPhone(empresa.telefone) : "",
            email: empresa.email || "",
            ativo: empresa.ativo !== false,
        });
        setLogoArquivo(null);
        setLogoPreviewErro(false);
        setLogoPreview(empresa.logoUrl ? config.urlArquivo(empresa.logoUrl) : "");
        if (logoInputRef.current) logoInputRef.current.value = "";
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ===================== Exclusão =====================

    const handleDelete = (empresa) => {
        setEmpresaParaExcluir(empresa);
        setErroMensagem("");
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = async () => {
        if (salvando) return;

        setShowConfirmDelete(false);
        setSalvando(true);
        try {
            const retorno = await empresaService.excluir(empresaParaExcluir.id);
            setSuccessMessage(retorno?.msg || "Empresa excluída com sucesso!");
            setShowSuccessDialog(true);
            if (editingId === empresaParaExcluir.id) limparFormulario();
            await carregarEmpresas(filtros);
        } catch (error) {
            console.error("Erro ao excluir empresa:", error);
            // 409: a empresa tem registros vinculados — a mensagem do backend
            // já explica o que impede a exclusão, então vai para a tela como veio.
            setErroMensagem(mensagemDoErro(error, "Erro ao excluir a empresa."));
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setEmpresaParaExcluir(null);
            setSalvando(false);
        }
    };

    // ===================== Provisionamento =====================

    const handleProvisionar = (empresa) => {
        setEmpresaParaProvisionar(empresa);
        setErroMensagem("");
        setShowConfirmProvisionar(true);
    };

    const handleConfirmProvisionar = async () => {
        if (salvando) return;

        setShowConfirmProvisionar(false);
        setSalvando(true);
        try {
            const retorno = await empresaService.provisionar(empresaParaProvisionar.id, {
                replicarCatalogo: true,
            });
            setSuccessMessage(retorno?.msg || "Cadastros de apoio replicados.");
            setShowSuccessDialog(true);
            await carregarEmpresas(filtros);
        } catch (error) {
            console.error("Erro ao replicar cadastros de apoio:", error);
            setErroMensagem(mensagemDoErro(error, "Erro ao replicar os cadastros de apoio."));
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setEmpresaParaProvisionar(null);
            setSalvando(false);
        }
    };

    // ===================== Logo =====================

    const handleLogoChange = (e) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;

        const tiposAceitos = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
        if (!tiposAceitos.includes(arquivo.type)) {
            setErroMensagem("Formato de logo inválido. Use PNG, JPG ou WEBP.");
            e.target.value = "";
            return;
        }

        setErroMensagem("");
        setLogoArquivo(arquivo);
        setLogoPreviewErro(false);
        setLogoPreview(URL.createObjectURL(arquivo));
    };

    const handleEnviarLogo = async () => {
        if (!logoArquivo || !editingId || logoSalvando) return;

        setLogoSalvando(true);
        setErroMensagem("");
        try {
            const retorno = await empresaService.enviarLogo(editingId, logoArquivo);
            setLogoArquivo(null);
            if (logoInputRef.current) logoInputRef.current.value = "";
            setLogoPreview(retorno?.logoUrl ? config.urlArquivo(retorno.logoUrl) : "");
            setSuccessMessage("Logo atualizada com sucesso!");
            setShowSuccessDialog(true);
            if (editingId === empresaDoUsuarioId && recarregarEmpresa) recarregarEmpresa();
            await carregarEmpresas(filtros);
        } catch (error) {
            console.error("Erro ao enviar a logo:", error);
            setErroMensagem(mensagemDoErro(error, "Erro ao enviar a logo da empresa."));
        } finally {
            setLogoSalvando(false);
        }
    };

    const handleRemoverLogo = async () => {
        if (!editingId || logoSalvando) return;

        setLogoSalvando(true);
        setErroMensagem("");
        try {
            await empresaService.removerLogo(editingId);
            setLogoArquivo(null);
            setLogoPreview("");
            if (logoInputRef.current) logoInputRef.current.value = "";
            setSuccessMessage("Logo removida com sucesso!");
            setShowSuccessDialog(true);
            if (editingId === empresaDoUsuarioId && recarregarEmpresa) recarregarEmpresa();
            await carregarEmpresas(filtros);
        } catch (error) {
            console.error("Erro ao remover a logo:", error);
            setErroMensagem(mensagemDoErro(error, "Erro ao remover a logo da empresa."));
        } finally {
            setLogoSalvando(false);
        }
    };

    // ===================== Filtros =====================

    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({ ...prev, [name]: value }));
    };

    const handleBuscar = (e) => {
        e.preventDefault();
        carregarEmpresas(filtros);
    };

    const handleLimparFiltros = () => {
        const limpos = { nome: "", ativo: "" };
        setFiltros(limpos);
        carregarEmpresas(limpos);
    };

    const handleBack = () => navigate("/configuracoes");

    // ===================== Render =====================

    const descreverTotais = (totais) => {
        if (!totais) return "—";
        const partes = Object.entries(rotulosTotais)
            .filter(([chave]) => (totais[chave] || 0) > 0)
            .map(([chave, rotulo]) => `${totais[chave]} ${rotulo}`);
        return partes.length ? partes.join(" · ") : "Sem registros";
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Empresas</h1>
                    <p className="text-sm text-stone-500 mt-1">
                        {podeGerenciarTodas
                            ? "Cadastre e mantenha as empresas atendidas pelo sistema."
                            : "Dados da empresa em que você está operando."}
                    </p>
                </div>

                {/* Faixa de erro: mostra a mensagem que veio do backend, sem detalhes técnicos. */}
                {erroMensagem && (
                    <div
                        role="alert"
                        className="flex items-start gap-3 bg-red-50 border border-red-300 text-red-800 text-sm rounded-md p-4"
                    >
                        <span className="flex-1">{erroMensagem}</span>
                        <button
                            type="button"
                            onClick={() => setErroMensagem("")}
                            className="text-red-800 font-medium focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
                            aria-label="Fechar aviso"
                        >
                            Fechar
                        </button>
                    </div>
                )}

                {/* Formulário de Criação/Edição */}
                {mostrarFormulario && (
                    <form
                        ref={formRef}
                        onSubmit={handleSubmitForm}
                        className="flex flex-col gap-6 bg-white p-4 md:p-6 rounded-md border border-slate-200"
                    >
                        <div className="text-stone-600 text-base font-medium">
                            {isEditMode ? "Editar Empresa" : "Cadastrar Nova Empresa"}
                        </div>

                        {/* ---------- Identificação ---------- */}
                        <fieldset className="flex flex-col gap-4">
                            <legend className="text-sm text-stone-500 font-semibold uppercase tracking-wide">
                                Identificação
                            </legend>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="nome" isRequired>Nome</FormLabel>
                                    <input
                                        id="nome"
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleInputChange}
                                        className={classeCampo(errors.nome)}
                                        placeholder="Nome da empresa"
                                    />
                                    {errors.nome && <p className="text-red-600 text-sm">{errors.nome}</p>}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="cnpj">CNPJ</FormLabel>
                                    <input
                                        id="cnpj"
                                        type="text"
                                        name="cnpj"
                                        value={formData.cnpj}
                                        onChange={handleCnpjChange}
                                        className={classeCampo(errors.cnpj)}
                                        placeholder="00.000.000/0000-00"
                                        inputMode="numeric"
                                    />
                                    {errors.cnpj && <p className="text-red-600 text-sm">{errors.cnpj}</p>}
                                </div>

                                <div className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                                    <FormLabel htmlFor="cnpjsAdicionais">
                                        CNPJs adicionais (filiais)
                                    </FormLabel>
                                    <textarea
                                        id="cnpjsAdicionais"
                                        name="cnpjsAdicionais"
                                        rows={3}
                                        value={formData.cnpjsAdicionais}
                                        onChange={handleInputChange}
                                        onBlur={handleCnpjsAdicionaisBlur}
                                        className={classeCampo(errors.cnpjsAdicionais)}
                                        placeholder={"Um CNPJ por linha"}
                                    />
                                    <p className="text-xs text-stone-500">
                                        Notas fiscais emitidas para estes CNPJs são capturadas para esta
                                        empresa, junto com as do CNPJ principal. Informe um por linha.
                                    </p>
                                    {errors.cnpjsAdicionais && (
                                        <p className="text-red-600 text-sm">{errors.cnpjsAdicionais}</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="razaoSocial">Razão Social</FormLabel>
                                    <input
                                        id="razaoSocial"
                                        type="text"
                                        name="razaoSocial"
                                        value={formData.razaoSocial}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="nomeFantasia">Nome Fantasia</FormLabel>
                                    <input
                                        id="nomeFantasia"
                                        type="text"
                                        name="nomeFantasia"
                                        value={formData.nomeFantasia}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="inscricaoEstadual">Inscrição Estadual</FormLabel>
                                    <input
                                        id="inscricaoEstadual"
                                        type="text"
                                        name="inscricaoEstadual"
                                        value={formData.inscricaoEstadual}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="inscricaoMunicipal">Inscrição Municipal</FormLabel>
                                    <input
                                        id="inscricaoMunicipal"
                                        type="text"
                                        name="inscricaoMunicipal"
                                        value={formData.inscricaoMunicipal}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                                    <input
                                        type="checkbox"
                                        name="ativo"
                                        checked={formData.ativo}
                                        onChange={handleInputChange}
                                        disabled={isEditMode && editingId === empresaDoUsuarioId}
                                        className="w-4 h-4 focus:outline-none focus:ring-2 focus:ring-stone-400 disabled:opacity-60"
                                    />
                                    Empresa ativa
                                </label>
                                {isEditMode && editingId === empresaDoUsuarioId && (
                                    <p className="text-xs text-stone-500">
                                        Não é possível desativar a empresa em que você está logado.
                                    </p>
                                )}
                            </div>
                        </fieldset>

                        {/* ---------- Endereço ---------- */}
                        <fieldset className="flex flex-col gap-4">
                            <legend className="text-sm text-stone-500 font-semibold uppercase tracking-wide">
                                Endereço
                            </legend>

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="cep">CEP</FormLabel>
                                    <input
                                        id="cep"
                                        type="text"
                                        name="cep"
                                        value={formData.cep}
                                        onChange={handleCepChange}
                                        className={classeCampo(false)}
                                        placeholder="00000-000"
                                        inputMode="numeric"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5 xl:col-span-2">
                                    <FormLabel htmlFor="endereco">Endereço</FormLabel>
                                    <input
                                        id="endereco"
                                        type="text"
                                        name="endereco"
                                        value={formData.endereco}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                        placeholder="Rua, número e complemento"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="bairro">Bairro</FormLabel>
                                    <input
                                        id="bairro"
                                        type="text"
                                        name="bairro"
                                        value={formData.bairro}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5 xl:col-span-2">
                                    <FormLabel htmlFor="municipio">Município</FormLabel>
                                    <input
                                        id="municipio"
                                        type="text"
                                        name="municipio"
                                        value={formData.municipio}
                                        onChange={handleInputChange}
                                        className={classeCampo(false)}
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="uf">UF</FormLabel>
                                    <input
                                        id="uf"
                                        type="text"
                                        name="uf"
                                        value={formData.uf}
                                        onChange={handleUfChange}
                                        className={classeCampo(errors.uf)}
                                        placeholder="RJ"
                                        maxLength={2}
                                    />
                                    {errors.uf && <p className="text-red-600 text-sm">{errors.uf}</p>}
                                </div>
                            </div>
                        </fieldset>

                        {/* ---------- Contato ---------- */}
                        <fieldset className="flex flex-col gap-4">
                            <legend className="text-sm text-stone-500 font-semibold uppercase tracking-wide">
                                Contato
                            </legend>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="telefone">Telefone</FormLabel>
                                    <input
                                        id="telefone"
                                        type="text"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleTelefoneChange}
                                        className={classeCampo(false)}
                                        placeholder="(21) 99999-9999"
                                        inputMode="numeric"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="email">E-mail</FormLabel>
                                    <input
                                        id="email"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={classeCampo(errors.email)}
                                        placeholder="contato@empresa.com.br"
                                    />
                                    {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}
                                </div>
                            </div>
                        </fieldset>

                        {/* ---------- Logo (só em edição: depende do id) ---------- */}
                        {isEditMode && podeAtualizar && (
                            <fieldset className="flex flex-col gap-4">
                                <legend className="text-sm text-stone-500 font-semibold uppercase tracking-wide">
                                    Logo
                                </legend>

                                <div className="flex flex-col md:flex-row md:items-center gap-4">
                                    <div className="w-40 h-24 border border-dashed border-slate-300 rounded-md flex items-center justify-center bg-stone-50 overflow-hidden">
                                        {logoPreview && !logoPreviewErro ? (
                                            <div className="relative w-full h-full flex items-center justify-center">
                                                <img
                                                    src={logoPreview}
                                                    alt={logoArquivo ? "Logo selecionada, ainda não enviada" : "Logo da empresa"}
                                                    className="max-w-full max-h-full object-contain"
                                                    onError={() => setLogoPreviewErro(true)}
                                                />
                                                {/* A pré-visualização de um arquivo recém-escolhido é
                                                    indistinguível de uma logo já gravada. O aviso existe
                                                    para que "selecionei" não seja confundido com "salvei". */}
                                                {logoArquivo && (
                                                    <span className="absolute bottom-0 inset-x-0 bg-amber-500 text-white text-[10px] text-center py-0.5">
                                                        Ainda não enviada
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-stone-400 text-center px-2">
                                                {logoPreviewErro
                                                    ? "Logo indisponível"
                                                    : "Sem logo cadastrada"}
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={handleLogoChange}
                                            className="text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 rounded"
                                            aria-label="Selecionar arquivo de logo"
                                        />
                                        <span className="text-xs text-stone-500">
                                            Formatos aceitos: PNG, JPG ou WEBP.
                                            {logoArquivo
                                                ? " A logo será enviada ao salvar a empresa."
                                                : ""}
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={handleEnviarLogo}
                                                disabled={!logoArquivo || logoSalvando}
                                                className={`${classeBotao} bg-stone-600`}
                                            >
                                                {logoSalvando ? "Enviando..." : "Enviar logo"}
                                            </button>
                                            {logoPreview && (
                                                <button
                                                    type="button"
                                                    onClick={handleRemoverLogo}
                                                    disabled={logoSalvando}
                                                    className={`${classeBotao} bg-red-600 focus:ring-red-500`}
                                                >
                                                    {logoSalvando ? "Removendo..." : "Remover logo"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* ---------- Provisionamento e administrador (só na criação) ---------- */}
                        {!isEditMode && podeGerenciarTodas && (
                            <fieldset className="flex flex-col gap-4 bg-stone-50 border border-slate-200 rounded-md p-4">
                                <legend className="text-sm text-stone-500 font-semibold uppercase tracking-wide px-1">
                                    Provisionamento
                                </legend>

                                <p className="text-sm text-stone-600">
                                    Ao salvar, os cadastros de apoio (status, classificações, modalidades,
                                    tipos, categorias, perfis e parâmetros) serão copiados da empresa modelo
                                    para a nova empresa, para que ela já nasça operável.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <FormLabel htmlFor="tenantModeloId">Empresa modelo</FormLabel>
                                        <select
                                            id="tenantModeloId"
                                            name="tenantModeloId"
                                            value={formData.tenantModeloId}
                                            onChange={handleInputChange}
                                            className={classeCampo(false)}
                                        >
                                            <option value="">Usar a minha empresa como modelo</option>
                                            {empresas.map((empresa) => (
                                                <option key={empresa.id} value={empresa.id}>
                                                    {empresa.nome}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-end">
                                        <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                                            <input
                                                type="checkbox"
                                                name="replicarCatalogo"
                                                checked={formData.replicarCatalogo}
                                                onChange={handleInputChange}
                                                className="w-4 h-4 focus:outline-none focus:ring-2 focus:ring-stone-400"
                                            />
                                            Replicar também o catálogo de materiais e serviços
                                        </label>
                                    </div>
                                </div>

                                <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
                                    <input
                                        type="checkbox"
                                        name="criarAdministrador"
                                        checked={formData.criarAdministrador}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 focus:outline-none focus:ring-2 focus:ring-stone-400"
                                    />
                                    Criar usuário administrador para esta empresa
                                </label>

                                {formData.criarAdministrador && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="flex flex-col gap-1.5">
                                            <FormLabel htmlFor="administradorNome" isRequired>Nome</FormLabel>
                                            <input
                                                id="administradorNome"
                                                type="text"
                                                name="administradorNome"
                                                value={formData.administradorNome}
                                                onChange={handleInputChange}
                                                className={classeCampo(errors.administradorNome)}
                                            />
                                            {errors.administradorNome && (
                                                <p className="text-red-600 text-sm">{errors.administradorNome}</p>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <FormLabel htmlFor="administradorEmail" isRequired>E-mail</FormLabel>
                                            <input
                                                id="administradorEmail"
                                                type="email"
                                                name="administradorEmail"
                                                value={formData.administradorEmail}
                                                onChange={handleInputChange}
                                                className={classeCampo(errors.administradorEmail)}
                                            />
                                            {errors.administradorEmail && (
                                                <p className="text-red-600 text-sm">{errors.administradorEmail}</p>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <FormLabel htmlFor="administradorSenha" isRequired>Senha</FormLabel>
                                            <input
                                                id="administradorSenha"
                                                type="password"
                                                name="administradorSenha"
                                                value={formData.administradorSenha}
                                                onChange={handleInputChange}
                                                className={classeCampo(errors.administradorSenha)}
                                                placeholder="Mínimo de 8 caracteres"
                                            />
                                            {errors.administradorSenha && (
                                                <p className="text-red-600 text-sm">{errors.administradorSenha}</p>
                                            )}
                                        </div>

                                        <p className="text-xs text-stone-500 md:col-span-3">
                                            O administrador entra com esta senha e é obrigado a trocá-la no
                                            primeiro acesso.
                                        </p>
                                    </div>
                                )}
                            </fieldset>
                        )}

                        {/* Botões do formulário */}
                        <div className="flex flex-wrap items-center gap-4">
                            {isEditMode && (
                                <button
                                    type="button"
                                    onClick={limparFormulario}
                                    className={`${classeBotao} bg-stone-400`}
                                >
                                    Cancelar
                                </button>
                            )}

                            {(isEditMode ? podeAtualizar : podeGerenciarTodas) && (
                                <button
                                    type="submit"
                                    disabled={salvando}
                                    className={`${classeBotao} bg-stone-600`}
                                >
                                    {salvando
                                        ? "Salvando..."
                                        : isEditMode
                                            ? "Salvar Alterações"
                                            : "Adicionar Empresa"}
                                </button>
                            )}
                        </div>
                    </form>
                )}

                {/* Lista de empresas */}
                <div className="bg-white p-4 md:p-6 rounded-md border border-slate-200 flex flex-col gap-4">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                        <div className="text-stone-600 text-base font-medium">
                            Lista de Empresas
                        </div>

                        {podeGerenciarTodas && (
                            <form onSubmit={handleBuscar} className="flex flex-col sm:flex-row sm:items-end gap-3">
                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="filtroNome">Nome</FormLabel>
                                    <input
                                        id="filtroNome"
                                        type="text"
                                        name="nome"
                                        value={filtros.nome}
                                        onChange={handleFiltroChange}
                                        className={classeCampo(false)}
                                        placeholder="Filtrar por nome"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <FormLabel htmlFor="filtroAtivo">Situação</FormLabel>
                                    <select
                                        id="filtroAtivo"
                                        name="ativo"
                                        value={filtros.ativo}
                                        onChange={handleFiltroChange}
                                        className={classeCampo(false)}
                                    >
                                        <option value="">Todas</option>
                                        <option value="true">Ativas</option>
                                        <option value="false">Inativas</option>
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="submit"
                                        disabled={carregandoLista}
                                        className={`${classeBotao} bg-stone-600`}
                                    >
                                        {carregandoLista ? "Buscando..." : "Buscar"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleLimparFiltros}
                                        disabled={carregandoLista}
                                        className={`${classeBotao} bg-stone-400`}
                                    >
                                        Limpar
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse text-sm">
                            <thead>
                                <tr className="bg-stone-100">
                                    <th className="px-4 py-2 border border-slate-200">Empresa</th>
                                    <th className="px-4 py-2 border border-slate-200">CNPJ</th>
                                    <th className="px-4 py-2 border border-slate-200">Município/UF</th>
                                    <th className="px-4 py-2 border border-slate-200">Contato</th>
                                    <th className="px-4 py-2 border border-slate-200">Situação</th>
                                    <th className="px-4 py-2 border border-slate-200">Registros</th>
                                    <th className="px-4 py-2 border border-slate-200">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregandoLista ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-6 text-center text-slate-600">
                                            Carregando empresas...
                                        </td>
                                    </tr>
                                ) : empresas.length > 0 ? (
                                    empresas.map((empresa) => (
                                        <tr key={empresa.id} className="border-b align-top">
                                            <td className="px-4 py-2 border border-slate-200">
                                                <div className="flex items-center gap-2">
                                                    {empresa.logoUrl && (
                                                        <img
                                                            src={config.urlArquivo(empresa.logoUrl)}
                                                            alt={empresa.nome}
                                                            className="w-8 h-8 object-contain"
                                                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                        />
                                                    )}
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-stone-800">
                                                            {empresa.nome}
                                                        </span>
                                                        {(empresa.nomeFantasia || empresa.razaoSocial) && (
                                                            <span className="text-xs text-stone-500">
                                                                {empresa.nomeFantasia || empresa.razaoSocial}
                                                            </span>
                                                        )}
                                                        {empresa.id === empresaDoUsuarioId && (
                                                            <span className="mt-1 self-start whitespace-nowrap text-[10px] uppercase tracking-wide bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">
                                                                Empresa atual
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span>{empresa.cnpj ? maskCpfCnpj(empresa.cnpj) : "—"}</span>
                                                    {empresa.cnpjsAdicionais?.length > 0 && (
                                                        <span
                                                            className="text-xs text-stone-500"
                                                            title={empresa.cnpjsAdicionais.map(maskCpfCnpj).join(", ")}
                                                        >
                                                            + {empresa.cnpjsAdicionais.length}{" "}
                                                            {empresa.cnpjsAdicionais.length === 1 ? "filial" : "filiais"}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                {empresa.municipio || empresa.uf
                                                    ? `${empresa.municipio || "—"}${empresa.uf ? `/${empresa.uf}` : ""}`
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                <div className="flex flex-col">
                                                    <span>{empresa.telefone ? maskPhone(empresa.telefone) : "—"}</span>
                                                    <span className="text-xs text-stone-500">{empresa.email || ""}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 whitespace-nowrap">
                                                <span
                                                    className={`text-xs font-medium rounded px-2 py-0.5 ${empresa.ativo
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-stone-200 text-stone-700"
                                                        }`}
                                                >
                                                    {empresa.ativo ? "Ativa" : "Inativa"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200 text-xs text-stone-600 min-w-[180px]">
                                                {descreverTotais(empresa.totais)}
                                            </td>
                                            <td className="px-4 py-2 border border-slate-200">
                                                <div className="flex flex-wrap gap-2">
                                                    {podeAtualizar && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEdit(empresa)}
                                                            className="px-2 py-1 bg-stone-600 text-white rounded-md text-sm whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-stone-500"
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {/* Replicar apoio e excluir não fazem sentido na própria
                                                        empresa — o backend recusa as duas operações. */}
                                                    {podeGerenciarTodas && empresa.id !== empresaDoUsuarioId && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleProvisionar(empresa)}
                                                                disabled={salvando}
                                                                className="px-2 py-1 bg-stone-500 text-white rounded-md text-sm whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-stone-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                                title="Replicar novamente os cadastros de apoio da empresa modelo"
                                                            >
                                                                Replicar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDelete(empresa)}
                                                                disabled={salvando}
                                                                className="px-2 py-1 bg-red-600 text-white rounded-md text-sm whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60 disabled:cursor-not-allowed"
                                                            >
                                                                Excluir
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-6 text-center text-slate-600">
                                            Nenhuma empresa encontrada.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="flex items-center w-full gap-4">
                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className={`${classeBotao} bg-stone-600 flex items-center gap-2`}
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="" />
                            <span>Voltar</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmar salvar */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={() => setShowConfirmDialog(false)}
                message={isEditMode ? "Deseja salvar as alterações desta empresa?" : "Deseja cadastrar esta empresa?"}
            />

            {/* Confirmar exclusão */}
            <ConfirmationModal
                isVisible={showConfirmDelete}
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setShowConfirmDelete(false);
                    setEmpresaParaExcluir(null);
                }}
                message={`Deseja excluir a empresa "${empresaParaExcluir?.nome || ""}"? Esta ação não pode ser desfeita.`}
                textButtonConfirm="Sim"
            />

            {/* Confirmar replicação dos cadastros de apoio */}
            <ConfirmationModal
                isVisible={showConfirmProvisionar}
                onConfirm={handleConfirmProvisionar}
                onCancel={() => {
                    setShowConfirmProvisionar(false);
                    setEmpresaParaProvisionar(null);
                }}
                message={`Replicar os cadastros de apoio da sua empresa para "${empresaParaProvisionar?.nome || ""}"?`}
                textButtonConfirm="Sim"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message={successMessage}
                onClose={() => setShowSuccessDialog(false)}
            />
        </Template>
    );
};

export default GerenciarEmpresas;
